import PropTypes from 'prop-types';
import React from 'react';

import { Button, Input, Select } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import useAlert from 'src/hooks/useAlert';

import styles from './LeadResourceCallForm.module.scss';

const YES_NO_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const STATE_OPTIONS = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
].map((state) => ({ label: state, value: state }));

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dateOfBirth: '',
  street: '',
  streetAddressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  pcpName: '',
  appointmentBooked: null,
  disqualificationReason: '',
  disqualifiedDate: '',
  disqualifiedTime: '',
  caretakerFirstName: '',
  caretakerLastName: '',
  caretakerPhoneNumber: '',
  caretakerRelationship: '',
  mbiNumber: '',
  recentA1cLevel: '',
  recentA1cDate: '',
  verifiedContractId: '',
  verifiedPlanId: '',
  primaryInsurancePayorId: '',
  primaryInsuranceStart: '',
  primaryInsuranceEnd: '',
  voiceOptIn: null,
  smsOptIn: null,
  emailOptIn: null,
};

const toValue = (value) => (value == null ? '' : `${value}`);

const toNullableValue = (value) => {
  const normalized = `${value ?? ''}`.trim();
  return normalized || null;
};

const toForm = (lead = {}) => ({
  firstName: toValue(lead.firstName),
  lastName: toValue(lead.lastName),
  phone: toValue(lead.phone),
  email: toValue(lead.email),
  dateOfBirth: toValue(lead.dateOfBirth),
  street: toValue(lead.street),
  streetAddressLine2: toValue(lead.streetAddressLine2),
  city: toValue(lead.city),
  state: toValue(lead.state),
  postalCode: toValue(lead.postalCode),
  pcpName: toValue(lead.pcpName),
  appointmentBooked: lead.appointmentBooked ?? null,
  disqualificationReason: toValue(lead.disqualificationReason),
  disqualifiedDate: toValue(lead.disqualifiedDate),
  disqualifiedTime: toValue(lead.disqualifiedTime),
  caretakerFirstName: toValue(lead.caretakerFirstName),
  caretakerLastName: toValue(lead.caretakerLastName),
  caretakerPhoneNumber: toValue(lead.caretakerPhoneNumber),
  caretakerRelationship: toValue(lead.caretakerRelationship),
  mbiNumber: toValue(lead.mbiNumber),
  recentA1cLevel: toValue(lead.recentA1cLevel),
  recentA1cDate: toValue(lead.recentA1cDate),
  verifiedContractId: toValue(lead.verifiedContractId),
  verifiedPlanId: toValue(lead.verifiedPlanId),
  primaryInsurancePayorId: toValue(lead.primaryInsurancePayorId),
  primaryInsuranceStart: toValue(lead.primaryInsuranceStart),
  primaryInsuranceEnd: toValue(lead.primaryInsuranceEnd),
  voiceOptIn: lead.voiceOptIn ?? null,
  smsOptIn: lead.smsOptIn ?? null,
  emailOptIn: lead.emailOptIn ?? null,
});

const getPayload = (form) => ({
  FirstName: toNullableValue(form.firstName),
  LastName: toNullableValue(form.lastName),
  Phone: toNullableValue(form.phone),
  Email: toNullableValue(form.email),
  Date_of_Birth__c: toNullableValue(form.dateOfBirth),
  Street: toNullableValue(form.street),
  Street_Address_Line_2__c: toNullableValue(form.streetAddressLine2),
  City: toNullableValue(form.city),
  State: toNullableValue(form.state),
  PostalCode: toNullableValue(form.postalCode),
  PCP_Name__c: toNullableValue(form.pcpName),
  appointmentBooked: form.appointmentBooked,
  Disqualification_Reason__c: toNullableValue(form.disqualificationReason),
  Disqualified_Date__c: toNullableValue(form.disqualifiedDate),
  Disqualified_Time__c: toNullableValue(form.disqualifiedTime),
  Caretaker_First_Name__c: toNullableValue(form.caretakerFirstName),
  Caretaker_Last_Name__c: toNullableValue(form.caretakerLastName),
  Caretaker_Phone_Number__c: toNullableValue(form.caretakerPhoneNumber),
  Caretaker_Relationship__c: toNullableValue(form.caretakerRelationship),
  MBI_Number__c: toNullableValue(form.mbiNumber),
  Recent_a1c_level__c: toNullableValue(form.recentA1cLevel),
  Recent_a1c_date__c: toNullableValue(form.recentA1cDate),
  Verified_Contract_Id__c: toNullableValue(form.verifiedContractId),
  Verified_Plan_ID__c: toNullableValue(form.verifiedPlanId),
  Primary_Insurance_Payor_ID__c: toNullableValue(form.primaryInsurancePayorId),
  Primary_Insurance_Start__c: toNullableValue(form.primaryInsuranceStart),
  Primary_Insurance_End__c: toNullableValue(form.primaryInsuranceEnd),
  voiceOptIn: form.voiceOptIn,
  smsOptIn: form.smsOptIn,
  emailOptIn: form.emailOptIn,
});

const LockedValue = ({ label, value }) => (
  <div className={styles.lockedValue}>
    <span>{label}</span>
    <strong>{toValue(value) || '-'}</strong>
  </div>
);

const NativeField = ({ id, label, type = 'text', value, onChange }) => (
  <label className={styles.nativeField} htmlFor={id}>
    <span>{label}</span>
    <input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} />
  </label>
);

const CheckboxField = ({ id, label, checked, onChange }) => (
  <label className={styles.checkboxField} htmlFor={id}>
    <input
      id={id}
      type="checkbox"
      checked={checked === true}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span>{label}</span>
  </label>
);

const LeadResourceCallForm = ({ leadResourceId, onLeadUpdated }) => {
  const [lead, setLead] = React.useState(null);
  const [loadedLeadResourceId, setLoadedLeadResourceId] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const { showAlert, AlertType } = useAlert();

  React.useEffect(() => {
    let isCanceled = false;

    const loadLead = async () => {
      if (!leadResourceId) return;
      setIsLoading(true);
      setLead(null);
      setLoadedLeadResourceId(null);
      setForm(EMPTY_FORM);
      setErrorText('');

      try {
        const response = await apiRequest({ endpoint: `leadResources/${leadResourceId}` });
        const body = await response.json();

        if (!response.ok) {
          throw new Error(getRestErrorMessage(body) || 'Lead resource could not be loaded');
        }

        if (isCanceled) return;
        setLead(body);
        setLoadedLeadResourceId(leadResourceId);
        setForm(toForm(body));
      } catch (error) {
        if (isCanceled) return;
        setErrorText(error?.message || 'Lead resource could not be loaded');
      } finally {
        if (!isCanceled) setIsLoading(false);
      }
    };

    loadLead();

    return () => {
      isCanceled = true;
    };
  }, [leadResourceId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedAppointmentBooked =
    YES_NO_OPTIONS.find((option) => option.value === form.appointmentBooked) || null;
  const selectedState = STATE_OPTIONS.find((option) => option.value === form.state) || null;
  const canSave = Boolean(
    leadResourceId && loadedLeadResourceId === leadResourceId && !isLoading && !errorText,
  );

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);

    try {
      const response = await apiRequest({
        endpoint: `leadResources/${leadResourceId}`,
        method: HttpMethods.Patch,
        body: getPayload(form),
      });
      const body = await response.json();

      if (!response.ok) {
        throw new Error(getRestErrorMessage(body) || 'Lead resource update failed');
      }

      setLead(body);
      setForm(toForm(body));
      onLeadUpdated?.(body);
      showAlert(AlertType.Success, '', 'Lead updated');
    } catch (error) {
      showAlert(AlertType.Error, '', error?.message || 'Lead resource update failed');
    } finally {
      setIsSaving(false);
    }
  };

  if (!leadResourceId) return null;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Lead resource</div>
          <h3 className={styles.title}>Regal call form</h3>
        </div>
        <Button
          id="lead-resource-call-save"
          text="Save"
          isLoading={isSaving}
          disabled={!canSave || isSaving}
          onClick={handleSave}
        />
      </div>

      {isLoading ? <div className={styles.message}>Loading lead...</div> : null}
      {errorText ? <div className={styles.error}>{errorText}</div> : null}

      {!isLoading && !errorText ? (
        <>
          <div className={styles.lockedGrid}>
            <LockedValue label="Status" value={lead?.status} />
            <LockedValue label="Business Unit" value={lead?.businessUnit} />
            <LockedValue label="Lead Source" value={lead?.leadSource} />
            <LockedValue label="Zing ID" value={lead?.zingId} />
            <LockedValue label="Salesforce Lead ID" value={lead?.salesforceLeadId} />
            <LockedValue label="Salesforce Account ID" value={lead?.salesforceAccountId} />
          </div>

          <div className={styles.section}>
            <h4>Contact</h4>
            <div className={styles.grid}>
              <Input
                id="lead-call-first-name"
                label="First Name"
                value={form.firstName}
                onTextChange={(value) => updateField('firstName', value)}
              />
              <Input
                id="lead-call-last-name"
                label="Last Name"
                value={form.lastName}
                onTextChange={(value) => updateField('lastName', value)}
              />
              <Input
                id="lead-call-phone"
                label="Patient Phone Number"
                value={form.phone}
                onTextChange={(value) => updateField('phone', value)}
              />
              <Input
                id="lead-call-email"
                label="Patient Email"
                value={form.email}
                onTextChange={(value) => updateField('email', value)}
              />
              <NativeField
                id="lead-call-dob"
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth}
                onChange={(value) => updateField('dateOfBirth', value)}
              />
              <Input
                id="lead-call-pcp"
                label="PCP Name"
                value={form.pcpName}
                onTextChange={(value) => updateField('pcpName', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h4>Address</h4>
            <div className={styles.grid}>
              <Input
                id="lead-call-street"
                label="Street"
                value={form.street}
                onTextChange={(value) => updateField('street', value)}
              />
              <Input
                id="lead-call-street-2"
                label="Street Address Line 2"
                value={form.streetAddressLine2}
                onTextChange={(value) => updateField('streetAddressLine2', value)}
              />
              <Input
                id="lead-call-city"
                label="City"
                value={form.city}
                onTextChange={(value) => updateField('city', value)}
              />
              <Select
                id="lead-call-state"
                label="State"
                placeholder="--None--"
                options={STATE_OPTIONS}
                value={selectedState}
                isSearchable
                onChange={(option) => updateField('state', option?.value || '')}
              />
              <Input
                id="lead-call-postal-code"
                label="Postal Code"
                value={form.postalCode}
                onTextChange={(value) => updateField('postalCode', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h4>Call Outcome</h4>
            <div className={styles.grid}>
              <Select
                id="lead-call-appointment-booked"
                label="Appointment Booked?"
                placeholder="--None--"
                options={YES_NO_OPTIONS}
                value={selectedAppointmentBooked}
                onChange={(option) => updateField('appointmentBooked', option?.value ?? null)}
              />
              <Input
                id="lead-call-disqualification-reason"
                label="Disqualification Reason"
                value={form.disqualificationReason}
                onTextChange={(value) => updateField('disqualificationReason', value)}
              />
              <NativeField
                id="lead-call-disqualified-date"
                label="Disqualified Date"
                type="date"
                value={form.disqualifiedDate}
                onChange={(value) => updateField('disqualifiedDate', value)}
              />
              <NativeField
                id="lead-call-disqualified-time"
                label="Disqualified Time"
                type="time"
                value={form.disqualifiedTime}
                onChange={(value) => updateField('disqualifiedTime', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h4>Caregiver</h4>
            <div className={styles.grid}>
              <Input
                id="lead-call-caretaker-first-name"
                label="Caretaker First Name"
                value={form.caretakerFirstName}
                onTextChange={(value) => updateField('caretakerFirstName', value)}
              />
              <Input
                id="lead-call-caretaker-last-name"
                label="Caretaker Last Name"
                value={form.caretakerLastName}
                onTextChange={(value) => updateField('caretakerLastName', value)}
              />
              <Input
                id="lead-call-caretaker-phone"
                label="Caretaker Phone Number"
                value={form.caretakerPhoneNumber}
                onTextChange={(value) => updateField('caretakerPhoneNumber', value)}
              />
              <Input
                id="lead-call-caretaker-relationship"
                label="Caretaker Relationship"
                value={form.caretakerRelationship}
                onTextChange={(value) => updateField('caretakerRelationship', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h4>Insurance</h4>
            <div className={styles.grid}>
              <Input
                id="lead-call-mbi"
                label="MBI Number"
                value={form.mbiNumber}
                onTextChange={(value) => updateField('mbiNumber', value)}
              />
              <Input
                id="lead-call-contract-id"
                label="Verified Contract ID"
                value={form.verifiedContractId}
                onTextChange={(value) => updateField('verifiedContractId', value)}
              />
              <Input
                id="lead-call-plan-id"
                label="Verified Plan ID"
                value={form.verifiedPlanId}
                onTextChange={(value) => updateField('verifiedPlanId', value)}
              />
              <Input
                id="lead-call-payor-id"
                label="Primary Insurance Payor ID"
                value={form.primaryInsurancePayorId}
                onTextChange={(value) => updateField('primaryInsurancePayorId', value)}
              />
              <NativeField
                id="lead-call-insurance-start"
                label="Primary Insurance Start"
                type="date"
                value={form.primaryInsuranceStart}
                onChange={(value) => updateField('primaryInsuranceStart', value)}
              />
              <NativeField
                id="lead-call-insurance-end"
                label="Primary Insurance End"
                type="date"
                value={form.primaryInsuranceEnd}
                onChange={(value) => updateField('primaryInsuranceEnd', value)}
              />
              <Input
                id="lead-call-a1c-level"
                label="Recent A1C Level"
                value={form.recentA1cLevel}
                onTextChange={(value) => updateField('recentA1cLevel', value)}
              />
              <NativeField
                id="lead-call-a1c-date"
                label="Recent A1C Date"
                type="date"
                value={form.recentA1cDate}
                onChange={(value) => updateField('recentA1cDate', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h4>Communication</h4>
            <div className={styles.checkboxGrid}>
              <CheckboxField
                id="lead-call-voice-opt-in"
                label="Voice Opt In"
                checked={form.voiceOptIn}
                onChange={(value) => updateField('voiceOptIn', value)}
              />
              <CheckboxField
                id="lead-call-sms-opt-in"
                label="SMS Opt In"
                checked={form.smsOptIn}
                onChange={(value) => updateField('smsOptIn', value)}
              />
              <CheckboxField
                id="lead-call-email-opt-in"
                label="Email Opt In"
                checked={form.emailOptIn}
                onChange={(value) => updateField('emailOptIn', value)}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

LockedValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

NativeField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

CheckboxField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

LeadResourceCallForm.propTypes = {
  leadResourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLeadUpdated: PropTypes.func,
};

export default LeadResourceCallForm;
